import React from "react";
import Marquee from "react-fast-marquee";
import { ArrowUpRight } from "@phosphor-icons/react";
import { DASHBOARD } from "@/constants/testIds";

const slugify = (s) =>
    s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

const SponsorTile = ({ sponsor, featured = false }) => {
    const slug = slugify(sponsor.name);
    const hasImageLogo = sponsor.logo_url && !sponsor.logo_url.startsWith("data:image/svg+xml");
    const isLink = Boolean(sponsor.website_url);

    const inner = (
        <div
            className={`group relative flex items-center gap-4 rounded-md border border-[#1f1f22] bg-[#0a0a0a] px-5 py-4 transition-all duration-200 ${
                isLink ? "hover:border-[#a3e635]/50 hover:bg-[#0d0f08]" : ""
            } ${featured ? "min-w-[340px]" : "min-w-[280px]"}`}
        >
            <div
                className={`flex flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#27272a] bg-black ${
                    featured ? "h-14 w-14" : "h-10 w-10"
                }`}
            >
                {hasImageLogo ? (
                    <img
                        src={sponsor.logo_url}
                        alt={`${sponsor.name} logo`}
                        className="h-full w-full object-contain"
                        loading="lazy"
                    />
                ) : (
                    <span className="font-mono-metric text-sm font-bold text-[#a3e635]">
                        {sponsor.name.charAt(0)}
                    </span>
                )}
            </div>
            <div className="flex min-w-0 flex-col leading-tight">
                <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm text-zinc-200">{sponsor.name}</span>
                    {isLink && (
                        <ArrowUpRight
                            size={12}
                            className="flex-shrink-0 text-zinc-500 transition-colors duration-200 group-hover:text-[#a3e635]"
                        />
                    )}
                </div>
                <span className="font-mono-metric text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                    {sponsor.underwriting}
                </span>
            </div>
            {featured && (
                <span className="ml-auto rounded-full border border-[#a3e635]/40 bg-[#0a0f05] px-2.5 py-0.5 font-mono-metric text-[9px] uppercase tracking-[0.24em] text-[#a3e635]">
                    Launch Partner
                </span>
            )}
        </div>
    );

    if (isLink) {
        return (
            <a
                key={sponsor.name}
                href={sponsor.website_url}
                target="_blank"
                rel="noopener noreferrer"
                title={sponsor.website_url.replace(/^https?:\/\//, "")}
                data-testid={DASHBOARD.sponsorCard(slug)}
                className="mx-4 block"
            >
                {inner}
            </a>
        );
    }
    return (
        <div key={sponsor.name} data-testid={DASHBOARD.sponsorCard(slug)} className="mx-4">
            {inner}
        </div>
    );
};

export const Sponsors = ({ sponsors = [] }) => {
    if (!sponsors.length) return null;
    // Feature the launch partner in a dedicated hero tile.
    // The marquee still shows ALL sponsors so CoaterZ scrolls with the rest.
    const featured = sponsors.find((s) => s.featured);

    return (
        <section
            data-testid={DASHBOARD.sponsors}
            className="rounded-lg border border-[#1f1f22] bg-[#080808] py-8"
        >
            <div className="mb-6 px-8 lg:px-10">
                <span className="eviq-chip">This month · Sponsored initiatives</span>
                <h2 className="mt-3 font-editorial text-2xl text-white sm:text-3xl">
                    Amplified by our partners.
                </h2>
            </div>

            {featured && (
                <div className="mb-6 px-8 lg:px-10">
                    <SponsorTile sponsor={featured} featured />
                </div>
            )}

            <Marquee
                gradient
                gradientColor="#080808"
                gradientWidth={80}
                speed={35}
                pauseOnHover
            >
                {sponsors.map((s) => (
                    <SponsorTile key={s.name} sponsor={s} />
                ))}
            </Marquee>
        </section>
    );
};

export default Sponsors;
